import React, { useEffect, useState } from "react";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import Modal from "../components/Modal";

const EMPTY = { username: "", full_name: "", email: "", password: "", role: "staff", is_active: true };

export default function Users() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [error, setError] = useState(null);

  const reload = () => {
    setLoading(true);
    api.listUsers().then(setUsers).finally(() => setLoading(false));
  };

  useEffect(reload, []);

  const onSave = async (u) => {
    setError(null);
    try {
      if (u.id) {
        await api.updateUser(u.id, {
          full_name: u.full_name,
          email: u.email || null,
          role: u.role,
          is_active: u.is_active,
        });
      } else {
        await api.createUser(u);
      }
      setModal(null);
      reload();
    } catch (err) {
      setError(err.message);
    }
  };

  const onDelete = async (u) => {
    if (!window.confirm(`Account "${u.username}" wirklich löschen?`)) return;
    try {
      await api.deleteUser(u.id);
      reload();
    } catch (err) {
      alert(err.message);
    }
  };

  const onResetPassword = async () => {
    if (resetPassword.length < 6) {
      setError("Mindestens 6 Zeichen");
      return;
    }
    try {
      await api.resetUserPassword(resetTarget.id, resetPassword);
      setResetTarget(null);
      setResetPassword("");
      setError(null);
      alert(`Neues Passwort gesetzt für ${resetTarget.username}`);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl">Mitarbeiter</h1>
          <p className="text-fh-gray text-sm mt-1">{users.length} Accounts</p>
        </div>
        <button onClick={() => setModal(EMPTY)} className="btn-primary">+ Neuer Mitarbeiter</button>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-fh-gray">Lade…</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {users.map((u) => (
              <li key={u.id} className="px-5 py-3 flex items-center justify-between flex-wrap gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{u.full_name}</span>
                    <span className={`badge ${u.role === "admin" ? "badge-admin" : "badge-staff"}`}>{u.role}</span>
                    {!u.is_active && <span className="badge bg-gray-200 text-gray-600">deaktiviert</span>}
                    {u.id === currentUser?.id && <span className="badge bg-fh-red/10 text-fh-red">du</span>}
                  </div>
                  <div className="text-xs text-fh-gray mt-0.5">
                    {u.username} · {u.email || "—"} · {u.id}
                    {u.last_login_at && ` · letzter Login: ${new Date(u.last_login_at).toLocaleDateString("de-DE")}`}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setModal({ ...u, password: "" })} className="btn-ghost text-sm">Bearbeiten</button>
                  <button onClick={() => setResetTarget(u)} className="btn-ghost text-sm">Passwort</button>
                  {u.id !== currentUser?.id && (
                    <button onClick={() => onDelete(u)} className="btn-ghost text-sm text-fh-red">Löschen</button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {modal && (
        <Modal
          open
          title={modal.id ? `Mitarbeiter bearbeiten · ${modal.username}` : "Neuer Mitarbeiter"}
          onClose={() => { setModal(null); setError(null); }}
          footer={
            <>
              <button className="btn-secondary" onClick={() => { setModal(null); setError(null); }}>Abbrechen</button>
              <button
                className="btn-primary"
                onClick={() => onSave(modal)}
                disabled={!modal.username || !modal.full_name || (!modal.id && (!modal.password || modal.password.length < 6))}
              >Speichern</button>
            </>
          }
        >
          {error && <div className="bg-fh-red/10 text-fh-red-dark text-sm px-3 py-2 rounded mb-3">{error}</div>}
          <div className="space-y-3">
            <div>
              <label className="label-field">Benutzername *</label>
              <input className="input-field" value={modal.username} onChange={(e) => setModal({ ...modal, username: e.target.value })} disabled={!!modal.id} />
            </div>
            <div>
              <label className="label-field">Voller Name *</label>
              <input className="input-field" value={modal.full_name} onChange={(e) => setModal({ ...modal, full_name: e.target.value })} />
            </div>
            <div>
              <label className="label-field">Email</label>
              <input type="email" className="input-field" value={modal.email || ""} onChange={(e) => setModal({ ...modal, email: e.target.value })} />
            </div>
            {!modal.id && (
              <div>
                <label className="label-field">Passwort * (mind. 6 Zeichen)</label>
                <input type="text" className="input-field font-mono" value={modal.password || ""} onChange={(e) => setModal({ ...modal, password: e.target.value })} />
                <p className="text-xs text-fh-gray mt-1">Notiere das Passwort und gib es dem Mitarbeiter weiter.</p>
              </div>
            )}
            <div>
              <label className="label-field">Rolle</label>
              <select className="input-field" value={modal.role} onChange={(e) => setModal({ ...modal, role: e.target.value })}>
                <option value="staff">Staff (nur Verleih)</option>
                <option value="admin">Admin (volle Rechte)</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 accent-fh-red"
                checked={modal.is_active}
                onChange={(e) => setModal({ ...modal, is_active: e.target.checked })}
              />
              <span>Account aktiv</span>
            </label>
          </div>
        </Modal>
      )}

      {resetTarget && (
        <Modal
          open
          title={`Passwort zurücksetzen · ${resetTarget.username}`}
          onClose={() => { setResetTarget(null); setResetPassword(""); setError(null); }}
          footer={
            <>
              <button className="btn-secondary" onClick={() => { setResetTarget(null); setResetPassword(""); setError(null); }}>Abbrechen</button>
              <button className="btn-primary" onClick={onResetPassword}>Passwort setzen</button>
            </>
          }
        >
          {error && <div className="bg-fh-red/10 text-fh-red-dark text-sm px-3 py-2 rounded mb-3">{error}</div>}
          <div>
            <label className="label-field">Neues Passwort (mind. 6 Zeichen)</label>
            <input type="text" className="input-field font-mono" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} />
            <p className="text-xs text-fh-gray mt-2">
              Notiere dir das Passwort und gib es {resetTarget.full_name} weiter. Es wird nicht angezeigt nachdem das Modal geschlossen ist.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
}
